import React, { useEffect, useState } from 'react';
import Component from 'src/helpers/Component/Component';
import Headline from 'src/helpers/Headline/Headline';
import {
  GetStaticComponentProps,
  LinkField,
  useComponentProps,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import RichTextWrapper from 'src/helpers/RichTextWrapper/RichTextWrapper';
import {
  MediaPrimary,
  MediaPrimaryStaticProps,
  MediaSecondary,
  MediaSecondaryStaticProps,
  getMediaPrimaryStaticProps,
  getMediaSecondaryStaticProps,
} from 'src/helpers/Media';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import { ContentBlockWithMediaTheme } from './ContentBlockWithMedia.theme';
import useExperienceEditor from 'lib/utils/use-experience-editor';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { cta1ToButtonProps, cta2ToButtonProps, cta3ToButtonProps } from 'src/helpers/Button';
import ButtonGroup from 'src/helpers/ButtonGroup/ButtonGroup';
import LinkWrapper from 'src/helpers/LinkWrapper/LinkWrapper';
import { useOnlineScheduling } from 'lib/forms/OnlineScheduling/OnlineSchedulingContext';

export type ContentBlockWithMediaProps =
  Feature.EnterpriseWeb.Enterprise.Components.General.ContentBlockWithMedia.ContentBlockWithMedia;

type CTALinkWrapperProps = {
  children: React.ReactNode;
  link: LinkField;
};

const CTALinkWrapper = ({ children, link }: CTALinkWrapperProps): JSX.Element =>
  link?.value?.href ? (
    <LinkWrapper
      field={link?.value}
      ariaLabel={{ value: link?.value?.text || 'CTA Link' }}
      suppressLinkText
    >
      {children}
    </LinkWrapper>
  ) : (
    <>{children}</>
  );

const ContentBlockWithMedia = (props: ContentBlockWithMediaProps): JSX.Element => {
  const isEE = useExperienceEditor();
  const { themeData } = useTheme(ContentBlockWithMediaTheme(props, props.fields));
  const { currentScreenWidth } = useCurrentScreenType();
  const componentProps = useComponentProps<MediaPrimaryStaticProps & MediaSecondaryStaticProps>(
    props.rendering.uid
  );
  const [inOSModal, setInOSModal] = useState(false);
  const onlineSchedulingContext = useOnlineScheduling();

  const isInOSModal = () => {
    try {
      return onlineSchedulingContext?.isInline ? true : window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  useEffect(() => {
    setInOSModal(isInOSModal());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineSchedulingContext?.isInline]);

  if (!props) {
    return <></>;
  }

  const imageLayoutType =
    props.fields?.primaryImage?.value?.src &&
    !props.fields?.secondaryImage?.value?.src &&
    currentScreenWidth >= getBreakpoint('md')
      ? 'intrinsic'
      : 'responsive';

  const imageContainerWidth =
    imageLayoutType === 'intrinsic'
      ? { maxWidth: `${props.fields?.primaryImage?.value?.width}px` }
      : {};

  const focusArea = 'top center';
  const isDesktop = currentScreenWidth >= getBreakpoint('md');

  const hasPrimaryVideo = props.fields?.primaryVideo;
  const hasSecondaryVideo = props.fields?.secondaryVideo;
  const hasPrimaryMedia = !!(props.fields?.primaryImage?.value?.src || hasPrimaryVideo);
  const hasSecondaryMedia = !!(props.fields?.secondaryImage?.value?.src || hasSecondaryVideo);

  const toggleOn = !!props.fields?.primaryCaptionStyling?.value;

  const primaryHasCaption =
    !!props.fields?.primaryImageCaption?.value &&
    props.fields?.primaryImageCaption?.value.trim() !== '';
  const secondaryHasCaption =
    !!props.fields?.secondaryImageCaption?.value &&
    props.fields?.secondaryImageCaption?.value.trim() !== '';

  const hasTwoMedia = hasPrimaryMedia && hasSecondaryMedia;

  // Decide where the primary caption goes
  let renderPrimaryUnderPrimary = false;
  let renderPrimaryUnderSecondary = false;
  let renderPrimaryFullWidth = false;

  if (primaryHasCaption) {
    if (hasTwoMedia) {
      if (secondaryHasCaption) {
        // Both captions present each under its own media
        renderPrimaryUnderPrimary = true;
      } else {
        // Only primary caption present
        if (toggleOn) {
          // desktop & mobile under primary only
          renderPrimaryUnderPrimary = true;
        } else {
          if (isDesktop) {
            // Desktop full width beneath both media
            renderPrimaryFullWidth = true;
          } else {
            // Mobile beneath secondary media
            renderPrimaryUnderSecondary = true;
          }
        }
      }
    } else {
      // Only primary media exists always under primary
      renderPrimaryUnderPrimary = true;
    }
  }

  // Secondary caption always under secondary media if present
  const renderSecondaryUnderSecondary = secondaryHasCaption;

  const getAspectRatioClass = (imageField?: { width?: number; height?: number }): string => {
    const width = imageField?.width;
    const height = imageField?.height;

    if (!width || !height) {
      return 'pt-[56.25%]'; // fallback 16:9
    }

    const ratio = width / height;
    if (Math.abs(ratio - 16 / 9) < 0.1) {
      return 'pt-[56.25%]';
    }
    if (Math.abs(ratio - 4 / 3) < 0.1) {
      return 'pt-[75%]';
    }
    if (Math.abs(ratio - 3 / 2) < 0.1) {
      return 'pt-[66.66%]';
    }
    if (Math.abs(ratio - 1) < 0.1) {
      return 'pt-[100%]';
    }
    return `pt-[${(100 / ratio).toFixed(2)}%]`;
  };

  return (
    <Component variant="lg" dataComponent="general/contentblockwithmedia" {...props}>
      <div
        className={
          inOSModal
            ? 'col-span-12 mt-xl grid grid-cols-12 md:gap-x-s'
            : 'col-span-12 grid grid-cols-12 md:gap-x-s'
        }
      >
        <div className={themeData.classes.headingContainer}>
          <Headline defaultTag="h2" classes={themeData.classes.headlineContainer} {...props} />
          <RichTextWrapper
            field={props.fields?.topCopy}
            className={themeData.classes.topCopyContainer}
          />
        </div>
        <div className={themeData.classes.bodyContainer}>
          <div className={themeData.classes.imageContainer}>
            <CTALinkWrapper link={props.fields?.primaryImageLink}>
              {(hasPrimaryMedia || isEE) && (
                <div
                  className={
                    hasPrimaryVideo
                      ? themeData.classes.videoOuterContainer
                      : themeData.classes.imageOuterContainer
                  }
                  style={imageContainerWidth}
                >
                  <div
                    className={`relative w-full overflow-hidden ${getAspectRatioClass(
                      props.fields?.primaryImage?.value
                    )}`}
                  >
                    <div className="absolute inset-0 h-full w-full">
                      <MediaPrimary
                        {...props}
                        imageLayout={imageLayoutType}
                        focusArea={focusArea}
                        shortsRatio={props.fields?.useYouTubeShortsRatio?.value}
                        staticProps={componentProps?.mediaPrimary}
                        hideCaption={true}
                      />
                    </div>
                  </div>
                  {renderPrimaryUnderPrimary && (
                    <RichTextWrapper
                      classes={`${themeData.classes.captionContainer} ${
                        props.fields?.useYouTubeShortsRatio?.value ? 'text-center' : ''
                      }`}
                      field={props.fields?.primaryImageCaption}
                    />
                  )}
                </div>
              )}
            </CTALinkWrapper>
            <CTALinkWrapper link={props.fields?.secondaryImageLink}>
              {(hasSecondaryMedia || isEE) && (
                <div
                  className={
                    hasSecondaryVideo
                      ? themeData.classes.videoOuterContainer
                      : themeData.classes.imageOuterContainer
                  }
                  style={imageContainerWidth}
                >
                  <div
                    className={`relative w-full overflow-hidden ${getAspectRatioClass(
                      props.fields?.secondaryImage?.value
                    )}`}
                  >
                    <div className="absolute inset-0 h-full w-full">
                      <MediaSecondary
                        {...props}
                        imageLayout={imageLayoutType}
                        focusArea={focusArea}
                        staticProps={componentProps?.mediaSeconary}
                        shortsRatio={props.fields?.useYouTubeShortsRatio?.value}
                        hideCaption={true}
                      />
                    </div>
                  </div>
                  {renderSecondaryUnderSecondary && (
                    <RichTextWrapper
                      classes={themeData.classes.captionContainer}
                      field={props.fields?.secondaryImageCaption}
                    />
                  )}
                  {renderPrimaryUnderSecondary && (
                    <RichTextWrapper
                      classes={themeData.classes.captionContainer}
                      field={props.fields?.primaryImageCaption}
                    />
                  )}
                </div>
              )}
            </CTALinkWrapper>
          </div>
          {renderPrimaryFullWidth && (
            <RichTextWrapper
              classes={`${themeData.classes.captionContainer} ${
                props.fields?.useYouTubeShortsRatio?.value ? 'text-center' : ''
              }`}
              field={props.fields?.primaryImageCaption}
            />
          )}
          <RichTextWrapper
            classes={themeData.classes.bodyContainer}
            field={props.fields?.bottomCopy}
          />
        </div>
        <div className={themeData.classes.contentWrapper}>
          <ButtonGroup
            cta1={cta1ToButtonProps(props, themeData.classes.buttonGroupClass.cta1Classes)}
            cta2={cta2ToButtonProps(props, themeData.classes.buttonGroupClass.cta2Classes)}
            cta3={cta3ToButtonProps(props, themeData.classes.buttonGroupClass.cta3Classes)}
            wrapperClasses={themeData.classes.buttonGroupClass.wrapper}
            ctaAlignment={props.fields?.ctaAlignment}
          />
        </div>
      </div>
    </Component>
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  const datasource = rendering as ContentBlockWithMediaProps;
  const mediaStaticProps: MediaPrimaryStaticProps & MediaSecondaryStaticProps = {};

  if (!datasource) {
    return mediaStaticProps;
  }

  mediaStaticProps.mediaPrimary = await getMediaPrimaryStaticProps(datasource);
  mediaStaticProps.mediaSeconary = await getMediaSecondaryStaticProps(datasource);

  return mediaStaticProps;
};

export default withDatasourceCheck()<ContentBlockWithMediaProps>(ContentBlockWithMedia);
